import { useEffect, useRef } from "react";
import { CheckCheck, Trash2 } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useNotifications } from "../NotificationProvider";
import { NotificationItem } from "./NotificationItem";

export function NotificationPanel() {
  const { t } = useTranslation();
  const {
    notifications,
    unreadCount,
    isOpen,
    closePanel,
    markAllAsRead,
    clearAll,
  } = useNotifications();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        const bell = document.getElementById("notification-bell");
        if (bell?.contains(e.target as Node)) return;
        closePanel();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePanel();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, closePanel]);

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className="card-shadow absolute right-0 top-full z-50 mt-2 w-[min(380px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-border bg-card"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">{t.notifications.title}</h3>
          {unreadCount > 0 && (
            <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-accent-foreground">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllAsRead}
              title={t.notifications.markAllRead}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent/20 hover:text-primary"
            >
              <CheckCheck size={16} />
            </button>
          )}
          {notifications.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              title={t.notifications.clearAll}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-card-muted hover:text-pink"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="max-h-[400px] overflow-y-auto p-2">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-card-muted">
              <CheckCheck size={24} className="text-muted" />
            </div>
            <p className="mt-3 text-sm text-muted">{t.notifications.empty}</p>
          </div>
        ) : (
          <div className="space-y-1">
            {notifications.map((notification) => (
              <NotificationItem key={notification.id} notification={notification} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
